import React, { useState, useEffect, useMemo } from "react";
import { useOpsContext } from "../../../ContextsAndProvider/OpsContext";
import OpsOrganizationPractitionerSelector from "./OpsOrganizationPractitionerSelector";
import { InfoOutlined } from "@mui/icons-material";
import { Col, Row, Card, Modal } from "react-bootstrap";
import { usregions, caregions } from "../../../utils/constants";
import { getDocumentDashboard } from "../../api/FillableDocument.js";
import { searchEncounters } from "../../apps/api/AppointmentRequests";
import { failed } from "../../common/Toastify";
import moment from "moment";
import PatientIcon from "../../../assets/images/patients.png";
import DocumentIcon from "../../../assets/images/document.png";
import UsersIcon from "../../../assets/images/users.png";
import AppointmentIcon from "../../../assets/images/appointmentsDashboard.png";
import OpsDashboardEncounterList from "./OpsDashboardEncounterList";
import OpsAppointmentsTab from "./OpsAppointmentsTab";
import OpsPatientsTab from "./OpsPatientsTab";
import OpsDocumentsTab from "./OpsDocumentsTab";
import "./OpsDashboard.css";

const TAB_ENCOUNTERS = "encounters";
const TAB_APPOINTMENTS = "appointments";
const TAB_PATIENTS = "patients";
const TAB_DOCUMENTS = "documents";

const OpsDashboardMain = () => {
  const { selectedOrganization, selectedPractitioner, organizations } = useOpsContext();
  const [showDashboard, setShowDashboard] = useState(false);
  const [selectedTab, setSelectedTab] = useState(TAB_ENCOUNTERS);
  const [encounterCount, setEncounterCount] = useState(0);
  const [documentCount, setDocumentCount] = useState(0);
  const [showScopeModal, setShowScopeModal] = useState(false);

  const scopeCodeToName = useMemo(() => {
    const all = [...(usregions || []), ...(caregions || [])];
    return Object.fromEntries(all.map((r) => [r.value, r.name]));
  }, []);

  useEffect(() => {
    setShowDashboard(!!selectedOrganization && !!selectedPractitioner);
  }, [selectedOrganization, selectedPractitioner]);

  const selectedOrgName = organizations?.find(
    (org) => org.id === selectedOrganization
  )?.name;

  const fetchDashboardData = async () => {
    if (!selectedOrganization || !selectedPractitioner?.value) return;
    try {
      const documentCountList = await getDocumentDashboard({
        status: "assigned",
        practitionerId: selectedPractitioner.value,
        orgId: selectedOrganization,
      });
      setDocumentCount(documentCountList?.data?.length || 0);

      const start = moment().subtract(30, "days").startOf("day").valueOf();
      const end = moment().endOf("day").valueOf();
      const encounterRes = await searchEncounters({
        orgId: selectedOrganization,
        practitionerId: selectedPractitioner.value,
        start,
        end,
        type: "open",
        page: 1,
        pageSize: 1,
        getAll: false,
      });
      setEncounterCount(encounterRes?.count?.Count || 0);
    } catch (err) {
      failed(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err.message
      );
    }
  };

  useEffect(() => {
    if (showDashboard) fetchDashboardData();
  }, [showDashboard, selectedOrganization, selectedPractitioner]);

  return (
    <div className="ops-dashboard-page-wrap">
      <section className="common-listing ops-dashboard-page">
        <div className="heading-wrap ops-dashboard-heading">
          <h1>Ops Dashboard - Practitioner Dashboard</h1>
        </div>
        <OpsOrganizationPractitionerSelector
          onSelectionComplete={(complete) => setShowDashboard(!!complete)}
        />
        {showDashboard && (
          <>
            <div className="ops-read-only-banner">
              <InfoOutlined />
              <span>
                Read-only view - You are viewing this dashboard as{" "}
                {selectedPractitioner?.label} from {selectedOrgName}
              </span>
            </div>

            {selectedPractitioner?.data && (
              <Card className="ops-practitioner-info-card" style={{ marginTop: 20, marginBottom: 24 }}>
                <Card.Body>
                  <h5>Practitioner Information</h5>
                  <div className="mt-3">
                    <p>
                      <strong>Name:</strong> {selectedPractitioner.data?.name?.[0]?.text || "-"}
                    </p>
                    <p>
                      <strong>ID:</strong> {selectedPractitioner.data?.id || selectedPractitioner.value || "-"}
                    </p>
                    <p>
                      <strong>Scope of Practice:</strong>{" "}
                      <button
                        type="button"
                        className="btn btn-link p-0 align-baseline text-primary text-decoration-underline"
                        onClick={() => setShowScopeModal(true)}
                      >
                        View scope of practice
                      </button>
                    </p>
                    <p>
                      <strong>Active:</strong> {selectedPractitioner.data?.active ? "Yes" : "No"}
                    </p>
                  </div>
                </Card.Body>
              </Card>
            )}

            <Modal
              show={showScopeModal}
              onHide={() => setShowScopeModal(false)}
              centered
              className="ops-scope-modal"
            >
              <Modal.Header closeButton>
                <Modal.Title>
                  Scope of practice of {selectedPractitioner?.data?.name?.[0]?.text || selectedPractitioner?.label || "Practitioner"}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
                {selectedPractitioner?.data?.scopeOfPractice &&
                Array.isArray(selectedPractitioner.data.scopeOfPractice) &&
                selectedPractitioner.data.scopeOfPractice.length > 0 ? (
                  <ul className="list-unstyled mb-0">
                    {selectedPractitioner.data.scopeOfPractice.map((code) => (
                      <li key={code} style={{ marginBottom: 8 }}>
                        {scopeCodeToName[code] ? `${scopeCodeToName[code]} (${code})` : `(${code})`}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className="text-muted">No scope of practice defined.</span>
                )}
              </Modal.Body>
            </Modal>

            <div className="ops-dashboard-content">
              <div className="ops-four-cards" style={{ marginBottom: 24 }}>
                <Row className="g-3">
                  <Col sm={6} md={3}>
                    <Card
                      className="ops-tab-card"
                      style={{
                        cursor: "pointer",
                        border: selectedTab === TAB_APPOINTMENTS ? "2px solid #005da8" : undefined,
                        background: selectedTab === TAB_APPOINTMENTS ? "#e9f5ff" : undefined,
                      }}
                      onClick={() => setSelectedTab(TAB_APPOINTMENTS)}
                    >
                      <Card.Body style={{ display: "flex", alignItems: "center" }}>
                        <img src={AppointmentIcon} alt="Appointments" style={{ width: 40, marginRight: 12 }} />
                        <div>
                          <h6 style={{ margin: 0 }}>Appointments</h6>
                          <small className="text-muted">View list</small>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col sm={6} md={3}>
                    <Card
                      className="ops-tab-card"
                      style={{
                        cursor: "pointer",
                        border: selectedTab === TAB_ENCOUNTERS ? "2px solid #005da8" : undefined,
                        background: selectedTab === TAB_ENCOUNTERS ? "#e9f5ff" : undefined,
                      }}
                      onClick={() => setSelectedTab(TAB_ENCOUNTERS)}
                    >
                      <Card.Body style={{ display: "flex", alignItems: "center" }}>
                        <img src={UsersIcon} alt="Encounters" style={{ width: 40, marginRight: 12 }} />
                        <div>
                          <h6 style={{ margin: 0 }}>Encounters</h6>
                          <small className="text-muted">{encounterCount} open</small>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col sm={6} md={3}>
                    <Card
                      className="ops-tab-card"
                      style={{
                        cursor: "pointer",
                        border: selectedTab === TAB_PATIENTS ? "2px solid #005da8" : undefined,
                        background: selectedTab === TAB_PATIENTS ? "#e9f5ff" : undefined,
                      }}
                      onClick={() => setSelectedTab(TAB_PATIENTS)}
                    >
                      <Card.Body style={{ display: "flex", alignItems: "center" }}>
                        <img src={PatientIcon} alt="Patients" style={{ width: 40, marginRight: 12 }} />
                        <div>
                          <h6 style={{ margin: 0 }}>Patients</h6>
                          <small className="text-muted">View list</small>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col sm={6} md={3}>
                    <Card
                      className="ops-tab-card"
                      style={{
                        cursor: "pointer",
                        border: selectedTab === TAB_DOCUMENTS ? "2px solid #005da8" : undefined,
                        background: selectedTab === TAB_DOCUMENTS ? "#e9f5ff" : undefined,
                      }}
                      onClick={() => setSelectedTab(TAB_DOCUMENTS)}
                    >
                      <Card.Body style={{ display: "flex", alignItems: "center" }}>
                        <img src={DocumentIcon} alt="Documents" style={{ width: 40, marginRight: 12 }} />
                        <div>
                          <h6 style={{ margin: 0 }}>Documents</h6>
                          <small className="text-muted">{documentCount} assigned</small>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </div>

              {selectedTab === TAB_ENCOUNTERS && <OpsDashboardEncounterList />}
              {selectedTab === TAB_APPOINTMENTS && <OpsAppointmentsTab />}
              {selectedTab === TAB_PATIENTS && <OpsPatientsTab />}
              {selectedTab === TAB_DOCUMENTS && <OpsDocumentsTab />}
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default OpsDashboardMain;
